/**
 * Normalizes and validates goal input fields (Baseline, Threshold, Target, Stretch).
 * Removes non-numeric characters, enforces max digits before/after decimal, and returns value and error.
 * @param rawValue - The input string from the user
 * @param maxIntegerDigits - Maximum digits before decimal (default 10)
 * @param maxFractionDigits - Maximum digits after decimal (default 2)
 * @returns { value: string, error: string }
 */
export function normalizeGoalInput(
  rawValue: string,
  maxIntegerDigits = 10,
  maxFractionDigits = 2
): { value: string; error: string } {
  let value = rawValue.replaceAll(/[^\d.]/g, '');
  const parts = value.split('.');
  if (parts.length > 2) {
    value = parts[0] + '.' + parts.slice(1).join('');
  }
  const [intPart, fracPart] = value.split('.');
  let error = '';
  if (intPart && intPart.length > maxIntegerDigits) {
    error = `Up to ${maxIntegerDigits} digits before decimal and up to ${maxFractionDigits} after decimal.`;
    value = intPart.slice(0, maxIntegerDigits) + (typeof fracPart === 'string' ? '.' + fracPart : '');
  } else if (fracPart && fracPart.length > maxFractionDigits) {
    error = `Up to ${maxIntegerDigits} digits before decimal and up to ${maxFractionDigits} after decimal.`;
    value = intPart + '.' + fracPart.slice(0, maxFractionDigits);
  }
  return { value, error };
}
import React, { useState, useMemo, useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
/**
 * Renders a textarea field with error display and helper text.
 * @param props - Field props
 */
type ErrorFieldProps = Readonly<{
  id: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  rows?: number;
  maxLength?: number;
  error?: boolean;
  errorMessage?: string;
  label?: React.ReactNode;
  labelClassName?: string;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  helperText?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}>;

const ErrorField: React.FC<Readonly<ErrorFieldProps>> = ({
  id,
  value,
  onChange,
  rows = 4,
  maxLength = FIELD_MAX,
  error,
  errorMessage,
  label,
  labelClassName = '',
  textareaRef,
  helperText,
  className = '',
  style = {},
}) => {
  return (
    <div className="textarea-wrapper w-full relative">
      {label && (
        <label htmlFor={id} className={labelClassName}>{label}</label>
      )}
      <textarea
        id={id}
        ref={textareaRef}
        value={value}
        onChange={onChange}
        rows={rows}
        maxLength={maxLength}
        className={`mt-1 bg-white w-full! border border-border-light rounded-md p-2${error ? ' field-error' : ''} ${className}`}
        style={style}
      />
      {error ? (
        <div className="error-description">
          <ul>
            <li>
              <img src="/assets/images/errorCircleSmall.svg" alt="Error icon" width="14" height="14" />
              <span className="screenreader-only">Error</span>
              {errorMessage}
            </li>
          </ul>
        </div>
      ) : null}
      {helperText}
    </div>
  );
}
import { useAppSelector } from '../../../../store/hooks';
import { selectFocusAreaList, selectMetricList } from '../../../../store/slices/ManageProjectSlice';
import Dropdown, { DropdownOption } from '../../../../components/common/Dropdown';
import ErrorBanner, { ValidationError } from '../../../../components/common/ErrorBanner';
import MultiCheckboxSelect from '../../../../components/common/MultiCheckboxSelect';
import CustomDatepicker from '../../../../components/common/DatePicker/Datepicker';
import ContentToggle from '../../../../components/common/ContentToggle';
import PopUp from '../../../../components/common/PopUp/PopUp';
import { validateDate, validateProject } from '../projectValidation';
import { convertDateToYYYYMMDD } from '../../../../utils/date';
import {
  performanceMetricHelperTextContent,
  toolUsingHelperText,
  teamDocumentationHelperText,
  toolAndTipsHelperText,
  ideaHelperText,
  linkToMetricsInformation,
  resultSummaryHelperText,
  annualROIHelperText,
  resultDescHelperText,
  learningPracticeHelperText,
  numeratoreHelperText,
  calculatedValueLabelHelperText,
  denominatorHelperText,
  additionalVarialbeLabelHelperText,
  goalSettingHelperTextContent,
  valueCompassHelperTextContent,
  focusAreaHelperTextContent,
  smartGoalHelperTextContent,
} from '../ProjectHelper/ProjectDetailsHelperText';
import {
  hasProjectFormChanged,
  createProjectFormSnapshot,
  type ProjectFormData,
} from '../../../../utils/ProjectDetails/projectDetailsValidation';


const defaultOption: DropdownOption = { id: 0, label: 'Select', value: 'Select' };


const statusOptions: DropdownOption[] = [
  { id: 'Active', label: 'Active', value: 'Active' },
  { id: 'Closed', label: 'Closed', value: 'Closed' },
];

const FIELD_MAX = 1024;
const PROJECT_NAME_MAX = 256;
const PROJECT_NAME_MAX_ERROR = 'Maximum size of 256 characters for the project name is already reached.';
const TEAM_DOCS_URL_ERROR = 'Enter a valid URL.';
const ANNUAL_ROI_MAX_INTEGER_DIGITS = 8;
const DECIMAL_MAX_FRACTION_DIGITS = 2;
const ANNUAL_ROI_FORMAT_ERROR = 'Up to 8 digits before decimal and up to 2 after decimal.';
const OUTCOME_MEASURE_FIELD_MAX = 30;

const getRemainingCharacterText = (value: string | null | undefined, maxLength: number): string => (
  `${Math.max(0, maxLength - (value?.length ?? 0))} of ${maxLength} character left`
);



// resultSummaryOptions, toolsOptions and valueCompassOptions will be derived from the
// `focusAreaList` payload (if provided) below using useMemo so they reflect API data.

function buildIsoDate(v?: string | null): string | undefined {
  if (!v) return undefined;
  // already ISO-like
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return undefined;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Convert various incoming date values into MM-DD-YYYY for the UI datepicker
function toDisplayDate(v?: string | null): string | undefined {
  const iso = buildIsoDate(v);
  if (!iso) return undefined;
  const parts = iso.split('-');
  if (parts.length !== 3) return undefined;
  const [yyyy, mm, dd] = parts;
  return `${mm}-${dd}-${yyyy}`;
}

function toNumberOrNull(v: any): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function mapOptionToId(opt: any): number | null {
  if (!opt) return null;
  return toNumberOrNull(opt?.value ?? opt?.id ?? opt) ?? null;
}

function getOptionKey(opt: any): string {
  if (opt === null || opt === undefined) return '';
  const v = opt?.value ?? opt?.id ?? opt;
  return v === null || v === undefined ? '' : String(v);
}

function shouldReplaceWithMatchedOption(current: any, matched: DropdownOption | null | undefined): boolean {
  if (!matched) return false;
  if (!current) return true;
  const currentKey = getOptionKey(current);
  const matchedKey = getOptionKey(matched);
  if (currentKey !== matchedKey) return true;
  return current?.id !== matched.id || current?.label !== matched.label;
}

function isDefaultDropdownOption(opt: DropdownOption | null | undefined): boolean {
  const optionKey = getOptionKey(opt);
  return optionKey === '' || optionKey === String(defaultOption.value) || optionKey === String(defaultOption.id);
}

function sanitizeDecimalInput(v: any, maxIntegerDigits: number = 10, maxFractionDigits: number = DECIMAL_MAX_FRACTION_DIGITS) {
  if (v === null || v === undefined) return '';
  let s = String(v);
  s = s.replaceAll(/[^0-9.]/g, '');
  const parts = s.split('.');
  const intPart = (parts[0] || '').slice(0, maxIntegerDigits);
  const fracPart = parts[1] ? parts[1].slice(0, maxFractionDigits) : '';
  return fracPart === '' ? intPart : `${intPart}.${fracPart}`;
}

function formatToOneDecimal(v: any) {
  if (v === null || v === undefined || v === '') return '';
  const num = Number(String(v).replaceAll(',', ''));
  if (Number.isNaN(num)) return String(v);
  return num.toFixed(1);
}

function normalizeExternalUrl(url: string): string {
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return '';
  if (/^https?:\/\//i.test(trimmedUrl)) {
    return trimmedUrl;
  }
  return `https://${trimmedUrl}`;
}

function validateExternalUrlInput(url: string): string {
  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return '';
  }

  if (!/^https?:\/\//i.test(trimmedUrl)) {
    return TEAM_DOCS_URL_ERROR;
  }

  try {
    const parsedUrl = new URL(trimmedUrl);
    if (!/^https?:$/i.test(parsedUrl.protocol)) {
      return TEAM_DOCS_URL_ERROR;
    }
    if (!parsedUrl.hostname) {
      return TEAM_DOCS_URL_ERROR;
    }
    return '';
  } catch {
    return TEAM_DOCS_URL_ERROR;
  }
}

export { buildIsoDate, toDisplayDate, toNumberOrNull, mapOptionToId, getOptionKey, sanitizeDecimalInput, formatToOneDecimal, validateExternalUrlInput };

interface ManageProjectContainerProps {
  project?: any;
  selectedProject?: any;
  isEditing?: boolean;
  focusAreaList?: any;
  onFocusAreaChange?: (focusId: any) => void;
  metricList?: any;
  onMetricChange?: (metricId: any) => void;
  datasourceList?: any[];
  actionContainerRef?: React.RefObject<HTMLDivElement>;
  onDataSourceChange?: (dataId: any) => void;
  onClose?: () => void;
  onSave?: (payload: any) => Promise<any>;
  onSetUnsavedChanges?: (hasChanges: boolean) => void;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const ManageProjectContainerContent: React.FC<ManageProjectContainerProps> = ({ project, selectedProject, isEditing, focusAreaList: propFocusAreaList, onFocusAreaChange, metricList: propMetricList, onMetricChange, datasourceList, onDataSourceChange, actionContainerRef, onClose, onSave, onSetUnsavedChanges } ) => {
  // Section 1
  const [projectName, setProjectName] = useState('');
  const [projectNameError, setProjectNameError] = useState('');
  const projectNameRef = useRef<HTMLInputElement | null>(null);
  const [valueCompass, setValueCompass] = useState<(string | number)[]>([]);
  const valueCompassRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<DropdownOption>(statusOptions[0]);
  const [focusArea, setFocusArea] = useState<DropdownOption>(defaultOption);
  const focusAreaRef = useRef<HTMLDivElement | null>(null);
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const startDateRef = useRef<HTMLDivElement | null>(null);
  const [startDateError, setStartDateError] = useState<string>('');
  const [completedDate, setCompletedDate] = useState<string | undefined>(undefined);
  const completedDateRef = useRef<HTMLDivElement | null>(null);
  const [completedDateError, setCompletedDateError] = useState<string>('');
  const [smartGoal, setSmartGoal] = useState('');
  const smartGoalRef = useRef<HTMLTextAreaElement | null>(null);

  

  // Section 2
  const [performanceMetric, setPerformanceMetric] = useState<DropdownOption>(defaultOption);
  const performanceMetricRef = useRef<HTMLDivElement | null>(null);
  const [pspGoal, setPspGoal] = useState('');
  const [dataSource, setDataSource] = useState<DropdownOption>(defaultOption);
  const dataSourceRef = useRef<HTMLDivElement | null>(null);

  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [baseline, setBaseline] = useState('');
  const [threshold, setThreshold] = useState('');
  const [target, setTarget] = useState('');
  const [stretch, setStretch] = useState('');
  const baselineRef = useRef<HTMLInputElement | null>(null);
  const thresholdRef = useRef<HTMLInputElement | null>(null);
  const targetRef = useRef<HTMLInputElement | null>(null);
  const stretchRef = useRef<HTMLInputElement | null>(null);
  const [baselineFormatError, setBaselineFormatError] = useState<string>('');
  const [thresholdFormatError, setThresholdFormatError] = useState<string>('');
  const [targetFormatError, setTargetFormatError] = useState<string>('');
  const [stretchFormatError, setStretchFormatError] = useState<string>('');

  const openExternalLink = useCallback((event: React.MouseEvent<HTMLAnchorElement>, href: string): void => {
    event.preventDefault();
    const normalizedHref = normalizeExternalUrl(href);
    if (!normalizedHref) {
      return;
    }

    globalThis.open(normalizedHref, '_blank', 'noopener,noreferrer');
  }, []);

  const getFieldErrorMessage = useCallback(
    (fieldName: string): string =>
      validationErrors.find((error) => error.field === fieldName)?.message ?? '',
    [validationErrors],
  );

  const hasFieldError = useCallback(
    (fieldName: string): boolean => Boolean(getFieldErrorMessage(fieldName)),
    [getFieldErrorMessage],
  );

  const clearFieldValidationError = useCallback((fieldName: string): void => {
    setValidationErrors((previous) => previous.filter((error) => error.field !== fieldName));
  }, []);

  const handleBannerErrorClick = useCallback((error: ValidationError): void => {
    const target = error.fieldRef?.current;
    if (!target) {
      return;
    }

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const isDirectlyFocusable =
      target.matches('input, textarea, select, button, [tabindex]');
    const focusableChild = target.querySelector<HTMLElement>(
      'input, textarea, select, button, [role="button"], [tabindex]',
    );
    const focusTarget = isDirectlyFocusable ? target : focusableChild;

    try {
      focusTarget?.focus({ preventScroll: true });
    } catch {
      try {
        focusTarget?.focus();
      } catch {}
    }
  }, []);

  // Section 3
  const [toolsSelected, setToolsSelected] = useState<(string | number)[]>([]);

  // Use focusAreaList passed from parent if provided, otherwise fall back to redux selector
  const reduxFocusAreaList = useAppSelector(selectFocusAreaList);
  const reduxMetricList = useAppSelector(selectMetricList);
  const focusAreaList = propFocusAreaList ?? reduxFocusAreaList ?? [];
  const metricList = propMetricList ?? reduxMetricList ?? [];


   const focusAreaOptions: DropdownOption[] = useMemo(() => {
    const items = Array.isArray(focusAreaList.focusAreas) ? focusAreaList.focusAreas : [];
    if (items.length === 0) return [defaultOption];
    return [
      defaultOption,
      ...items.map((it: any) => {
        const id = it.FOCUSID ?? it.focusId ?? it.id ?? it.vcId ?? it.value ?? String(it);
        const label = it.FOCUSAREADESC ?? it.focusAreaDesc ?? it.focusArea ?? String(it);
        return { id, label, value: id } as DropdownOption;
      }),
    ];
  }, [focusAreaList]);

  const performanceMetricOptions: DropdownOption[] = useMemo(() => {
    const items = Array.isArray(metricList.metrics) ? metricList.metrics : [];
    if (items.length === 0) return [defaultOption];
    return [
      defaultOption,
      ...items.map((it: any) => {
        const id = it.METRICID ?? it.metricId ?? it.id ?? it.value ?? it.metric ?? String(it);
        const label = it.METRIC ?? it.metricDesc ?? it.metric ?? String(it);
        return { id, label, value: id } as DropdownOption;
      }),
    ];
  }, [metricList]);

  // derive PSPGOAL from the selected performance metric using metricList.metrics
  useEffect(() => {
    // debug: trace when this effect runs
    // eslint-disable-next-line no-console
    console.debug('[MPC] useEffect: derivePspGoal', { performanceMetric, metricList });
    const items = Array.isArray(metricList?.metrics) ? metricList.metrics : [];
    const selId = (performanceMetric?.value ?? performanceMetric?.id ?? '') as any;
    if (!selId) {
      setPspGoal('');
      return;
    }
    const found = items.find((it: any) => String(it.METRICID ?? it.metricId ?? it.id ?? '') === String(selId));
    const goal = found ? String(found.PSPGOAL ?? '') : '';
    setPspGoal(goal);
  }, [performanceMetric, metricList]);

  const selectedMetricObj = useMemo(() => {
    const items = Array.isArray(metricList?.metrics) ? metricList.metrics : [];
    const selId = (performanceMetric?.value ?? performanceMetric?.id ?? '') as any;
    if (!selId) return undefined;
    return items.find((it: any) => String(it.METRICID ?? it.metricId ?? it.id ?? '') === String(selId));
  }, [performanceMetric, metricList]);

  const showOtherMetric = useMemo(() => {
    return Boolean(selectedMetricObj && String(selectedMetricObj.OTHERMETRIC ?? '').toUpperCase() === 'Y');
  }, [selectedMetricObj]);

  const hasSelectedFocusArea = useMemo(() => !isDefaultDropdownOption(focusArea), [focusArea]);

  const hasSelectedPerformanceMetric = useMemo(
    () => !isDefaultDropdownOption(performanceMetric),
    [performanceMetric],
  );

  const dataSourceOptionsFromProp: DropdownOption[] = useMemo(() => {
    // Accept either an array or an object containing `dataSources` (API returns { dataSources: [...] })
    let items: any[] = [];
    if (Array.isArray(datasourceList)) {
      items = datasourceList;
    } else if (Array.isArray((datasourceList as unknown as { dataSources?: any[] })?.dataSources)) {
      items = (datasourceList as unknown as { dataSources?: any[] }).dataSources ?? [];
    } else {
      items = [];
    }
    if (items.length === 0) return [defaultOption];
    return [
      defaultOption,
      ...items.map((it: any) => {
        const id = it.DATAID ?? it.dataId ?? it.sourceId ?? it.id ?? it.value ?? String(it);
        const label = it.DATADESC ?? it.dataDesc ?? it.sourceDesc ?? it.desc ?? it.label ?? String(it);
        return { id, label, value: id } as DropdownOption;
      }),
    ];
  }, [datasourceList]);

  const valueCompassOptions: DropdownOption[] = useMemo(() => {
    const items = Array.isArray(focusAreaList.valueCompassStatic) ? focusAreaList.valueCompassStatic : [];
    if (items.length === 0) return [];
    return items.map((it: any) => {
        const id = it.VC_ID ?? it.vcId ?? it.id ?? String(it);
        const label = it.VC_DESC ?? it.vcDesc ?? it.VC_DESC ?? String(it);
        return { id, label, value: id } as DropdownOption;
      });
  }, [focusAreaList]);

  const toolsOptions: DropdownOption[] = useMemo(() => {
    const items = Array.isArray(focusAreaList.performanceTools) ? focusAreaList.performanceTools : [];
    if (items.length === 0) return [];
    return items.map((it: any) => {
      const id = it.PERFORMANCETOOLS_ID ?? it.id ?? String(it);
      const label = it.PERFORMANCETOOLS_DESC ?? it.description ?? String(it);
      return { id, label, value: id } as DropdownOption;
    });
  }, [focusAreaList]);

  const resultSummaryOptions: DropdownOption[] = useMemo(() => {
    const items = Array.isArray(focusAreaList.results) ? focusAreaList.results : [];
    if (items.length === 0) return [];
    return items.map((it: any) => {
      const id = it.RESULTID ?? it.RESULTCODE ?? it.id ?? String(it);
      const label = it.RESULTDESC ?? it.RESULTDESC_TXT ?? String(it);
      return { id, label, value: id } as DropdownOption;
    });
  }, [focusAreaList]);

  // links and resources — derive from datasourceList when available
  const dataSourcesArray = useMemo(() => {
    if (Array.isArray(datasourceList)) return datasourceList;
    if (Array.isArray((datasourceList as unknown as { dataSources?: any[] })?.dataSources)) {
      return (datasourceList as unknown as { dataSources?: any[] }).dataSources ?? [];
    }
    return [] as any[];
  }, [datasourceList]);

  const selectedDataSourceObj = useMemo(() => {
    if (!dataSourcesArray || dataSourcesArray.length === 0) return undefined;
    const selId = (dataSource?.value ?? dataSource?.id ?? '') as any;
    // treat the default `Select` option as no-selection
    const isDefaultSelected = selId === defaultOption.value || selId === defaultOption.id || selId === '' || selId === 0;
    if (!isDefaultSelected) {
      return dataSourcesArray.find((it: any) => String(it.DATAID ?? it.dataId ?? it.id ?? '') === String(selId));
    }
    return dataSourcesArray[0];
  }, [dataSourcesArray, dataSource]);

  const resourceLinks = useMemo(() => {
    const links: { label: string; href: string }[] = [];
    if (selectedDataSourceObj) {
      const websiteUrl = normalizeExternalUrl(String(selectedDataSourceObj.WEBSITE_URL ?? ''));
      const smartbookUrl = normalizeExternalUrl(String(selectedDataSourceObj.SMARTBOOK_URL ?? ''));
      const ideaBookUrl = normalizeExternalUrl(String(selectedDataSourceObj.IDEABOOK_URL ?? ''));
      const wikiUrl = normalizeExternalUrl(String(selectedDataSourceObj.WIKI_URL ?? ''));

      if (websiteUrl) links.push({ label: 'LMP Website', href: websiteUrl });
      if (smartbookUrl) links.push({ label: 'SmartBook', href: smartbookUrl });
      if (ideaBookUrl) links.push({ label: 'SharePoint', href: ideaBookUrl });
      if (wikiUrl) links.push({ label: 'PI wiki', href: wikiUrl });
      return links;
    }

    // Fallback: when editing an existing project there may be URL fields on the
    // project payload (websiteUrl, smartbookUrl, ideaBookUrl, wikiUrl, dataChartsUrl, metricUrl).
    // Map them to the same link labels so the UI shows the expected resources.
    const proj = selectedProject ?? project;
    if (!proj) return links;
    const websiteUrl = normalizeExternalUrl(String(proj.websiteUrl ?? ''));
    const smartbookUrl = normalizeExternalUrl(String(proj.smartbookUrl ?? ''));
    const ideaBookUrl = normalizeExternalUrl(String(proj.ideaBookUrl ?? ''));
    const wikiUrl = normalizeExternalUrl(String(proj.wikiUrl ?? ''));

    if (websiteUrl) links.push({ label: 'LMP Website', href: websiteUrl });
    if (smartbookUrl) links.push({ label: 'SmartBook', href: smartbookUrl });
    if (ideaBookUrl) links.push({ label: 'SharePoint', href: ideaBookUrl });
    if (wikiUrl) links.push({ label: 'PI wiki', href: wikiUrl });
    return links;
  // Only use project-level fallback links when in editing mode; include
  // `isEditing`, `selectedProject`, and `project` in deps so memo updates
  // when editing state or payloads change.
  }, [selectedDataSourceObj, isEditing, selectedProject, project]);

  const toolsAndTipsLink = useMemo(() => {
    const sourceUrl = normalizeExternalUrl(String(selectedDataSourceObj?.DATA_CHARTS_URL ?? ''));
    if (sourceUrl) {
      return { label: 'Data Charts', href: sourceUrl } as { label: string; href: string };
    }

    const projectUrl = normalizeExternalUrl(String((selectedProject?.dataChartsUrl ?? project?.dataChartsUrl) ?? ''));
    if (projectUrl) {
      return { label: 'Data Charts', href: projectUrl } as { label: string; href: string };
    }

    return undefined;
  }, [project?.dataChartsUrl, selectedDataSourceObj, selectedProject?.dataChartsUrl]);

  const reportLink = useMemo(() => {
    const sourceUrl = normalizeExternalUrl(String(selectedDataSourceObj?.METRIC_URL ?? ''));
    if (sourceUrl) {
      return { label: 'Metric Report', href: sourceUrl } as { label: string; href: string };
    }

    const projectUrl = normalizeExternalUrl(String((selectedProject?.metricUrl ?? project?.metricUrl) ?? ''));
    if (projectUrl) {
      return { label: 'Metric Report', href: projectUrl } as { label: string; href: string };
    }

    return undefined;
  }, [project?.metricUrl, selectedDataSourceObj, selectedProject?.metricUrl]);
  

  const showDataSourceDescription = useMemo(() => {
    if (isDefaultDropdownOption(dataSource)) return false;
    return Boolean(selectedDataSourceObj && String(selectedDataSourceObj.OTHERDATASOURCE ?? '').toUpperCase() === 'Y');
  }, [dataSource, selectedDataSourceObj]);

  // Smaller helpers to keep prepopulation readable and low complexity.
  const parseValueCompassRaw = (raw: any): string[] => {
    if (raw === undefined || raw === null) return [];
    if (Array.isArray(raw)) return raw.map((v: any) => String(v).trim()).filter(Boolean);
    if (typeof raw === 'string') return raw.split(',').map((s) => s.trim()).filter(Boolean);
    return [String(raw).trim()].filter(Boolean);
  };

  const matchValueCompassCandidates = (ids: string[], labels: string[]): (string | number)[] => {
    const candidates = valueCompassOptions.filter((o) => o?.value !== undefined && String(o.value) !== String(defaultOption.value));
    if (ids.length > 0) {
      const matched = candidates.filter((o) => ids.includes(String(o.value))).map((o) => o.value as string | number);
      if (matched.length > 0) return matched;
    }
    if (labels.length > 0) {
      const lower = new Set(labels.map((l) => String(l).toLowerCase()));
      const matched = candidates.filter((o) => lower.has(String(o.label).toLowerCase())).map((o) => o.value as string | number);
      if (matched.length > 0) return matched;
    }
    // fallback: prefer ids when present, otherwise labels
    return ids.length > 0 ? ids.map(String) : labels.map(String);
  };

  const normalizeValueCompassIds = (items: (string | number)[]): (string | number)[] => {
    const optionIdsAreNumbers = valueCompassOptions.some((o) => typeof o.id === 'number');
    return items.map((s) => {
      if (optionIdsAreNumbers) {
        const n = Number(s);
        return Number.isNaN(n) ? String(s) : n;
      }
      return String(s);
    });
  };

  const applyProjectName = () => {
    const newProjectName = selectedProject?.pipDesc ?? project?.pipDesc ?? '';
    if (projectName !== newProjectName) setProjectName(newProjectName);
  };

  const applyValueCompass = () => {
    if (!(selectedProject?.valueCompassId || project?.valueCompassId || selectedProject?.valueCompassDesc || project?.valueCompassDesc)) return;
    const rawId = selectedProject?.valueCompassId ?? project?.valueCompassId;
    const rawDesc = selectedProject?.valueCompassDesc ?? project?.valueCompassDesc;

    const ids = parseValueCompassRaw(rawId);
    const labels = parseValueCompassRaw(rawDesc);
    const finalMatched = matchValueCompassCandidates(ids, labels);
    const newVC = normalizeValueCompassIds(finalMatched);

    const curVC = Array.isArray(valueCompass) ? valueCompass.map(String) : [];
    const newVCS = newVC.map(String);
    const sameVC = curVC.length === newVCS.length && curVC.every((v, i) => v === newVCS[i]);
    if (!sameVC) setValueCompass(newVC);
  };

  const applyStatus = () => {
    const incomingPerfStatus = String(selectedProject?.perfStatus ?? project?.perfStatus ?? '');
    const normalizedStatusValue = ['Complete', 'Closed'].includes(incomingPerfStatus)
      ? 'Closed'
      : incomingPerfStatus;
    const st = statusOptions.find((o) => String(o.value) === normalizedStatusValue)
      ?? statusOptions.find((o) => String(o.id) === normalizedStatusValue);
    if (getOptionKey(status) !== getOptionKey(st ?? defaultOption)) setStatus(st ?? defaultOption);
  };

  const applyFocusArea = () => {
    const focusId = selectedProject?.focusId ?? project?.focusId;
    const fa = focusAreaOptions.find((o) => String(o.value) === String(focusId) || String(o.id) === String(focusId));
    if (getOptionKey(focusArea) !== getOptionKey(fa ?? defaultOption)) setFocusArea(fa ?? defaultOption);
    try {
      if (typeof onFocusAreaChange === 'function') {
        const fid = focusId === undefined || focusId === null ? '' : String(focusId);
        if (lastNotifiedFocusIdRef.current !== fid) {
          onFocusAreaChange(fid);
          lastNotifiedFocusIdRef.current = fid;
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('onFocusAreaChange failed during prepopulate:', err);
    }
  };

  const applyDates = () => {
    setStartDate(toDisplayDate(selectedProject?.perfStartDate ?? project?.perfStartDate) ?? undefined);
    setCompletedDate(toDisplayDate(selectedProject?.perfEndDate ?? project?.perfEndDate) ?? undefined);
  };

  const applySmartGoal = () => {
    setSmartGoal(selectedProject?.perfGoal ?? project?.perfGoal ?? null);
  };

  const applyPerformanceMetric = () => {
    const metricId = selectedProject?.metricId ?? project?.metricId;
    // `metric` is the human-readable label returned alongside `metricId` in the project payload.
    const metricLabel = selectedProject?.metric ?? project?.metric;
    const pm = performanceMetricOptions.find((o) => String(o.value) === String(metricId) || String(o.id) === String(metricId));
    // When metric options haven't loaded yet, use the label from the project payload so the
    // dropdown shows the correct text immediately instead of showing "Select" or the raw ID.
    // The "direct preselect" effect will swap this for the proper matched option once options arrive.
    const pmToSet = pm ?? (metricId && metricLabel
      ? { id: String(metricId), label: metricLabel, value: String(metricId) } as DropdownOption
      : defaultOption);
    if (shouldReplaceWithMatchedOption(performanceMetric, pmToSet)) setPerformanceMetric(pmToSet);
  };

  const applyPspGoal = () => {
    setPspGoal(String(selectedProject?.pspGoal ?? project?.pspGoal ?? null));
  };

  const applyDataSourceNotify = () => {
    try {
      const dataId = selectedProject?.dataId ?? project?.dataId ?? null;
      if (typeof onDataSourceChange === 'function') {
        const did = dataId === undefined || dataId === null ? '' : String(dataId);
        if (lastNotifiedDataIdRef.current !== did) {
          onDataSourceChange(did);
          lastNotifiedDataIdRef.current = did;
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('onDataSourceChange failed during prepopulate:', err);
    }
  };

  const applyDescriptions = () => {
    setMetricDescription(selectedProject?.perfOtherMetricText ?? project?.perfOtherMetricText ?? '');
    setDataSourceDescription(selectedProject?.perfOtherDataSourceText ?? project?.perfOtherDataSourceText ?? '');
  };

  const applyGoalNumbers = () => {
    setBaseline(formatToOneDecimal(selectedProject?.improvementBaseline ?? project?.improvementBaseline ?? ''));
    setTarget(formatToOneDecimal(selectedProject?.improvementTarget ?? project?.improvementTarget ?? ''));
    setThreshold(formatToOneDecimal(selectedProject?.improvementThreshold ?? project?.improvementThreshold ?? ''));
    setStretch(formatToOneDecimal(selectedProject?.improvementStretch ?? project?.improvementStretch ?? ''));
  };

  const applyToolsUsed = () => {
    if (!(selectedProject?.listOfToolsUsed || project?.listOfToolsUsed)) return;
    const rawTools = selectedProject?.listOfToolsUsed ?? project?.listOfToolsUsed;
    let entries: string[] = [];
    if (typeof rawTools === 'string') entries = rawTools.split(',').map((s: string) => s.trim()).filter(Boolean);
    else if (Array.isArray(rawTools)) entries = rawTools.map((v: any) => String(v).trim()).filter(Boolean);
    else if (rawTools !== null && rawTools !== undefined) entries = [String(rawTools).trim()];

    const normalized = entries.map((s) => String(s).trim()).filter(Boolean);
    const matchedById = toolsOptions.filter((o) => o?.id !== undefined).filter((o) => normalized.includes(String(o.id))).map((o) => o.id);
    const lowerSet = new Set(normalized.map((s) => s.toLowerCase()));
    const matchedByLabel = toolsOptions.filter((o) => o?.label !== undefined).filter((o) => lowerSet.has(String(o.label).toLowerCase())).map((o) => o.id);

    const combined = Array.from(new Set([...matchedById, ...matchedByLabel]));
    if (combined.length > 0) setToolsSelected(combined);
    else {
      const optionIdsAreNumbers = toolsOptions.some((o) => typeof o.id === 'number');
      if (optionIdsAreNumbers) {
        setToolsSelected(normalized.map((s) => {
          const n = Number(s);
          return Number.isNaN(n) ? s : n;
        }));
      } else setToolsSelected(normalized.map(String));
    }
  };

  const applyResultAndMisc = () => {
    const resId = selectedProject?.resultId ?? project?.resultId ?? null;
    const rs = resultSummaryOptions.find((o) => String(o.value) === String(resId) || String(o.id) === String(resId));
    setResultSummary(rs ?? defaultResultSummaryOption ?? defaultOption);
    setResultDescription(selectedProject?.pipResultDesc ?? project?.pipResultDesc ?? '');

    setAnnualRoi(selectedProject?.roi ?? project?.roi ?? '');
    setAnnualRoiFormatError('');
    setLearnings(selectedProject?.perfLearnings ?? project?.perfLearnings ?? '');

    setNumerator(selectedProject?.numeratorName ?? project?.numeratorName ?? '');
    setDenominator(selectedProject?.denominatorName ?? project?.denominatorName ?? '');
    setCalculatedValue(selectedProject?.valueName ?? project?.valueName ?? '');
    setAdditionalVar(selectedProject?.outsideVarName ?? project?.outsideVarName ?? '');

    setTeamDocs(selectedProject?.urlToTeam ?? project?.urlToTeam ?? '');
  };

  // Extracted prepopulate logic to reduce complexity in the effect above.
  const prepopulateFromSource = () => {
    applyProjectName();
    applyValueCompass();
    applyStatus();
    applyFocusArea();
    applyDates();
    applySmartGoal();
    applyPerformanceMetric();
    applyPspGoal();
    applyDataSourceNotify();
    applyDescriptions();
    applyGoalNumbers();
    applyToolsUsed();
    applyResultAndMisc();
  };

  // Prepopulate fields when editing an existing project, and re-populate whenever
  // the user switches to a different project while staying in edit mode.
  //
  // In React 17 async context, fetchProjectDetails (which updates `project` via
  // Redux) and setSelectedProject (which updates `selectedProject` local state)
  // fire in separate renders. We guard against the intermediate mixed-state render
  // by waiting until both share the same perfId before populating form fields.
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.debug('[MPC] useEffect: prepopulate start', { isEditing, selectedProjectId: selectedProject?.perfId ?? selectedProject?.id ?? null, projectDetailId: project?.perfId ?? project?.id ?? null, prepopulated: prepopulatedRef.current });

    if (!isEditing) {
      prepopulatedRef.current = false;
      setIsPrepopulationComplete(false);
      lastPrepopulatedProjectIdRef.current = null;
      return;
    }

    // Stable identity key driven by selectedProject (the user's explicit selection).
    const currentProjectId = String(
      selectedProject?.perfId ?? selectedProject?.id ?? selectedProject?.projectId ?? ''
    );

    // When the project identity changes, reset all guards so the new project
    // gets a full, fresh prepopulation on the next eligible render.
    if (currentProjectId && currentProjectId !== lastPrepopulatedProjectIdRef.current) {
      prepopulatedRef.current = false;
      allowDependentAutoSelectionRef.current = true;
      lastNotifiedFocusIdRef.current = null;
      lastNotifiedMetricIdRef.current = null;
      lastNotifiedDataIdRef.current = null;
      // Clear the stale datasource dropdown immediately so the previous project's
      // value is not shown while the new datasource list is loading.
      setDataSource(defaultOption);
    }

    if (prepopulatedRef.current) return;

    // Wait until projectDetails (project) and selectedProject both reflect the
    // same project. While they differ we are in the intermediate async render
    // where only one of the two props has updated — using mixed data would populate
    // the form with values from two different projects.
    const detailId = String(project?.perfId ?? project?.id ?? project?.projectId ?? '');
    if (currentProjectId && detailId && currentProjectId !== detailId) return;

    prepopulateFromSource();
    prepopulatedRef.current = true;
    setIsPrepopulationComplete(true);
    if (currentProjectId) {
      lastPrepopulatedProjectIdRef.current = currentProjectId;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, selectedProject, project]);

  // When a focus area is selected while editing, if the project has a metricId
  // prepopulate the Performance Metric dropdown once metric options are available.
  useEffect(() => {
    // debug: trace when focus-area->metric effect runs
    // eslint-disable-next-line no-console
    console.debug('[MPC] useEffect: focus-area metric prepopulate', { isEditing, focusArea, performanceMetricOptionsLength: performanceMetricOptions.length, selectedProjectId: selectedProject?.id ?? selectedProject?.projectId ?? null });
    if (!isEditing) return;
    if (!allowDependentAutoSelectionRef.current) return;
    const metricId = selectedProject?.metricId ?? project?.metricId;
    if (!metricId) return;
    // require a focusArea selection (ignore default)
    const focusSel = (focusArea?.value ?? focusArea?.id ?? '') as any;
    if (!focusSel || focusSel === defaultOption.value || focusSel === defaultOption.id) return;

    const midStr = String(metricId);
    const metricIdAsNumber = toNumberOrNull(metricId);
    const pmMatch = performanceMetricOptions.find((o) => String(o.value) === midStr || String(o.id) === midStr);
    if (pmMatch) {
      if (shouldReplaceWithMatchedOption(performanceMetric, pmMatch)) setPerformanceMetric(pmMatch);
    } else {
      // Metric not found in current options — either they haven't loaded yet or belong
      // to a different focus area. Never set a raw-ID fallback (that would show the
      // numeric ID as the dropdown label). The "direct preselect" effect below will
      // resolve the correct label once the right metric options arrive.
      return;
    }

    try {
      if (
        typeof onMetricChange === 'function' &&
        metricIdAsNumber !== null &&
        lastNotifiedMetricIdRef.current !== midStr
      ) {
        // defer to avoid racing with other prepopulate notifications
        setTimeout(() => {
          try {
            onMetricChange(metricIdAsNumber);
            lastNotifiedMetricIdRef.current = midStr;
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('onMetricChange failed in focus-area effect:', err);
          }
        }, 0);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to notify metric change in focus-area effect:', err);
    }
  }, [isEditing, focusArea, performanceMetricOptions, selectedProject, project, onMetricChange]);
  

  // Directly preselect the performance metric from the project when editing.
  // Runs whenever performanceMetricOptions changes (i.e. when the async list loads)
  // so it correctly resolves the selected value even when options arrive after the
  // initial prepopulation pass.
  useEffect(() => {
    if (!isEditing) return;
    const metricId = selectedProject?.metricId ?? project?.metricId;
    if (!metricId) return;
    const midStr = String(metricId);
    const metricIdAsNumber = toNumberOrNull(metricId);

    // Wait until real options have loaded before committing.
    const pmMatch = performanceMetricOptions.find((o) => String(o.value) === midStr || String(o.id) === midStr);

    // Guard: if the user has manually changed Focus Area or Performance Metric,
    // skip re-populating the dropdown so the manually reset defaultOption is not
    // overwritten with the stale selectedProject.metricId. However, we must still
    // notify the parent via onMetricChange when the current metric already reflects
    // this project's metricId — otherwise the data source list is never fetched and
    // the Data Source dropdown remains permanently locked.
    if (!allowDependentAutoSelectionRef.current) {
      // If the matched option is already selected (prepopulation completed before options loaded),
      // ensure the parent is notified so the datasource list is fetched.
      if (
        pmMatch &&
        hasSelectedFocusArea &&
        typeof onMetricChange === 'function' &&
        metricIdAsNumber !== null &&
        lastNotifiedMetricIdRef.current !== midStr
      ) {
        const currentId = String(performanceMetric?.value ?? performanceMetric?.id ?? '');
        if (currentId === midStr) {
          setTimeout(() => {
            try {
              onMetricChange(metricIdAsNumber);
              lastNotifiedMetricIdRef.current = midStr;
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error('onMetricChange failed in direct preselect effect (post-snapshot):', err);
            }
          }, 0);
        }
      }
      return;
    }

    // Only auto-select when the user hasn't manually changed the metric.
    // Safe to update when: still at defaultOption, or the current value already
    // reflects this project's metricId (was set by a previous prepopulation run).
    const currentId = String(performanceMetric?.value ?? performanceMetric?.id ?? '');
    const userHasChanged = !isDefaultDropdownOption(performanceMetric) && currentId !== midStr;
    if (userHasChanged) return;

    if (!pmMatch) return;

    if (shouldReplaceWithMatchedOption(performanceMetric, pmMatch)) {
      setPerformanceMetric(pmMatch);
    }

    if (
      hasSelectedFocusArea &&
      typeof onMetricChange === 'function' &&
      metricIdAsNumber !== null &&
      lastNotifiedMetricIdRef.current !== midStr
    ) {
      // defer notify to avoid racing with other prepopulate effects
      setTimeout(() => {
        try {
          onMetricChange(metricIdAsNumber);
          lastNotifiedMetricIdRef.current = midStr;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('onMetricChange failed in direct preselect effect:', err);
        }
      }, 0);
    }
  }, [isEditing, hasSelectedFocusArea, performanceMetric, performanceMetricOptions, selectedProject, project, onMetricChange]);

  // Directly preselect the data source from the project when editing.
  // Runs whenever dataSourceOptionsFromProp changes (i.e. when the async list loads)
  // so it correctly resolves the selected value even when options arrive after the
  // initial prepopulation pass.
  // Never uses a fallback label from project fields: the `project.dataDesc` field
  // may contain a description string rather than the dropdown option label.
  useEffect(() => {
    if (!isEditing) return;
    const dataId = selectedProject?.dataId ?? project?.dataId;
    if (!dataId) return;
    const didStr = String(dataId);

    // Only auto-select when the user hasn't manually changed the data source.
    // Safe to update when: still at defaultOption, or the current value already
    // reflects this project's dataId (was set by a previous prepopulation run).
    const currentId = String(dataSource?.value ?? dataSource?.id ?? '');
    const userHasChanged = !isDefaultDropdownOption(dataSource) && currentId !== didStr;
    if (userHasChanged) return;

    // Only update the dropdown when a real option is available in the fetched list.
    // If the list has not loaded yet, keep defaultOption and wait for the next run.
    const dsMatch = dataSourceOptionsFromProp.find((o) => String(o.value) === didStr || String(o.id) === didStr);
    if (dsMatch && shouldReplaceWithMatchedOption(dataSource, dsMatch)) {
      setDataSource(dsMatch);
    }

    if (typeof onDataSourceChange === 'function' && lastNotifiedDataIdRef.current !== didStr) {
      // defer notify to avoid racing with other prepopulate effects
      setTimeout(() => {
        try {
          onDataSourceChange(didStr);
          lastNotifiedDataIdRef.current = didStr;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('onDataSourceChange failed in direct preselect effect:', err);
        }
      }, 0);
    }
  }, [isEditing, dataSourceOptionsFromProp, selectedProject, project, onDataSourceChange]);

  
  // Section 4
  const [resultSummary, setResultSummary] = useState<DropdownOption>(defaultOption);
  const [annualRoi, setAnnualRoi] = useState('');
  const [annualRoiFormatError, setAnnualRoiFormatError] = useState('');
  const [resultDescription, setResultDescription] = useState('');
  const resultDescriptionRef = useRef<HTMLTextAreaElement | null>(null);
  const [metricDescription, setMetricDescription] = useState('');
  const [dataSourceDescription, setDataSourceDescription] = useState('');
  const [learnings, setLearnings] = useState('');
  const learningsRef = useRef<HTMLTextAreaElement | null>(null);
  const annualRoiRef = useRef<HTMLInputElement | null>(null);
  const metricDescriptionRef = useRef<HTMLTextAreaElement | null>(null);
  const dataSourceDescriptionRef = useRef<HTMLTextAreaElement | null>(null);

  // Section 5
  const [numerator, setNumerator] = useState('');
  const [calculatedValue, setCalculatedValue] = useState('');
  const [denominator, setDenominator] = useState('');
  const [additionalVar, setAdditionalVar] = useState('');

  // Section 6
  const [teamDocs, setTeamDocs] = useState('');
  const [teamDocsError, setTeamDocsError] = useState('');
  const teamDocsRef = useRef<HTMLInputElement | null>(null);

  // ===== Form Change Tracking =====
  const initialFormSnapshotRef = useRef<Partial<ProjectFormData> | null>(null);
  const isFormOpenRef = useRef<boolean>(false);
  const [isPrepopulationComplete, setIsPrepopulationComplete] = useState<boolean>(false);

  /**
   * Get current form data for change comparison
   */
  const getCurrentFormData = useCallback((): Partial<ProjectFormData> => {
    return {
      projectName,
      valueCompass,
      status,
      focusArea,
      startDate,
      completedDate,
      smartGoal,
      performanceMetric,
      pspGoal,
      dataSource,
      baseline,
      threshold,
      target,
      stretch,
      metricDescription,
      dataSourceDescription,
      toolsSelected,
      resultSummary,
      resultDescription,
      annualRoi,
      learnings,
      numerator,
      denominator,
      calculatedValue,
      additionalVar,
      teamDocs,
    };
  }, [
    projectName,
    valueCompass,
    status,
    focusArea,
    startDate,
    completedDate,
    smartGoal,
    performanceMetric,
    pspGoal,
    dataSource,
    baseline,
    threshold,
    target,
    stretch,
    metricDescription,
    dataSourceDescription,
    toolsSelected,
    resultSummary,
    resultDescription,
    annualRoi,
    learnings,
    numerator,
    denominator,
    calculatedValue,
    additionalVar,
    teamDocs,
  ]);

  /**
   * Create initial snapshot at the right time:
   * - For Add mode: immediately when form opens
   * - For Edit mode: after prepopulation completes
   */
  useEffect(() => {
    // Skip if snapshot already exists
    if (initialFormSnapshotRef.current) {
      return;
    }

    // For Add mode (not editing): create snapshot immediately
    if (!isEditing) {
      const currentData = getCurrentFormData();
      initialFormSnapshotRef.current = createProjectFormSnapshot(currentData);
      isFormOpenRef.current = true;
      return;
    }

    // For Edit mode: wait until prepopulation completes
    if (isEditing && isPrepopulationComplete) {
      setTimeout(() => {
        const finalData = getCurrentFormData();
        initialFormSnapshotRef.current = createProjectFormSnapshot(finalData);
        isFormOpenRef.current = true;
        
        allowDependentAutoSelectionRef.current = false;
      }, 150);
    }
  }, [isEditing, isPrepopulationComplete]);

  /**
   * Track form changes and notify parent component
   */
  useEffect(() => {
    // Only track changes after the form has been opened and snapshot created
    if (!isFormOpenRef.current || !initialFormSnapshotRef.current) {
      return;
    }

    const currentData = getCurrentFormData();
    const hasChanges = hasProjectFormChanged(currentData, initialFormSnapshotRef.current);

    if (typeof onSetUnsavedChanges === 'function') {
      onSetUnsavedChanges(hasChanges);
    }
  }, [
    onSetUnsavedChanges,
    projectName,
    valueCompass,
    status,
    focusArea,
    startDate,
    completedDate,
    smartGoal,
    performanceMetric,
    pspGoal,
    dataSource,
    baseline,
    threshold,
    target,
    stretch,
    metricDescription,
    dataSourceDescription,
    toolsSelected,
    resultSummary,
    resultDescription,
    annualRoi,
    learnings,
    numerator,
    denominator,
    calculatedValue,
    additionalVar,
    teamDocs,
  ]);

  /**
   * Reset unsaved changes state when form is closed or saved successfully
   */
  const resetUnsavedChanges = useCallback(() => {
    initialFormSnapshotRef.current = null;
    isFormOpenRef.current = false;
    setIsPrepopulationComplete(false);
    if (typeof onSetUnsavedChanges === 'function') {
      onSetUnsavedChanges(false);
    }
  }, [onSetUnsavedChanges]);
  // ===== End Form Change Tracking =====

  const defaultResultSummaryOption = useMemo(() => {
    const preferred = resultSummaryOptions.find(
      (o) => String(o.label).trim().toLowerCase() === 'results not available',
    );
    return preferred ?? resultSummaryOptions[0];
  }, [resultSummaryOptions]);

  const hasValidResultSummarySelection = useMemo(() => {
    return resultSummaryOptions.some((o) => String(o.id) === String(resultSummary?.id));
  }, [resultSummaryOptions, resultSummary?.id]);

  useEffect(() => {
    if (!defaultResultSummaryOption) return;
    if (isEditing) return;
    const hasSelected = resultSummaryOptions.some((o) => String(o.id) === String(resultSummary?.id));
    if (!hasSelected) {
      setResultSummary(defaultResultSummaryOption);
    }
  }, [defaultResultSummaryOption, resultSummaryOptions, resultSummary?.id, isEditing]);

  const handleSave = async () => {
    // (removed unused filterOutCustomFields)
    const payload = buildMappedPayload();
    if (hasInlineFormatErrors()) {
      focusFirstInlineFormatField();
      return;
    }

    // Custom validation for Result description and Learnings when status is Closed
    let customErrors: ValidationError[] = [];
    if (status?.value === 'Closed' || status?.id === 'Closed') {
      if (!resultDescription.trim()) {
        customErrors.push({
          field: 'Result description',
          message: 'Result description is required.',
          fieldRef: resultDescriptionRef,
        });
      }
      if (!learnings.trim()) {
        customErrors.push({
          field: 'Learning or successful practices',
          message: 'Learning or successful practices is required.',
          fieldRef: learningsRef,
        });
      }
    }

    // Run existing validation
    const valid = validateAndHandleErrors();

    // Merge custom errors with standard validation errors
    if (customErrors.length > 0 || !valid) {
      setValidationErrors((prev) => {
        // Remove any previous custom errors for these fields
        const filteredPrev = prev.filter(
          (e) => e.field !== 'Result description' && e.field !== 'Learning or successful practices'
        );
        return [...filteredPrev, ...customErrors];
      });
      setTimeout(() => {
        const banner = errorBannerRef.current;
        if (banner && typeof banner.scrollIntoView === 'function') {
          banner.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);
      return;
    }

    // If the team docs URL has an inline error, surface it in the form's error banner
    // and block the API call until the user corrects the URL.
    if (teamDocsError) {
      setValidationErrors([{ field: 'Team Project Documentation', message: teamDocsError, fieldRef: teamDocsRef as React.RefObject<HTMLElement> }]);
      setTimeout(() => {
        const banner = errorBannerRef.current;
        if (banner && typeof banner.scrollIntoView === 'function') {
          banner.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);
      return;
    }
    // If parent provided an onSave handler, call it and await its result so parent
    // (DetailsContainer) can perform the API call with proper headers (nuid) and
    // any further state refresh. If successful, call onClose to hide the manage view.
    if (onSave) return await callOnSave(payload);
    // eslint-disable-next-line no-console
    console.log('ContentToggles save payload', payload);
  };

  // helpers extracted from handleSave to reduce cognitive complexity

  const validateAndHandleErrors = (): boolean => {
    const refs = {
      projectNameRef,
      valueCompassRef,
      focusAreaRef,
      startDateRef,
      completedDateRef,
      smartGoalRef,
      performanceMetricRef,
      dataSourceRef,
      metricDescriptionRef,
      dataSourceDescriptionRef,
      statusRef: null,
    };
    const errors = validateProject(
      {
        projectName,
        valueCompass,
        focusArea,
        startDate,
        completedDate,
        smartGoal,
        performanceMetric,
        dataSource,
        status,
        metricDescription,
        dataSourceDescription,
        requireMetricDescription: showOtherMetric,
        requireDataSourceDescription: showDataSourceDescription,
      },
      refs,
    );
    if (errors.length > 0) {
      setValidationErrors(errors);
      setTimeout(() => {
        const banner = errorBannerRef.current;
        if (banner && typeof banner.scrollIntoView === 'function') {
          banner.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);
      return false;
    }
    setValidationErrors([]);
    return true;
  };

  const hasInlineFormatErrors = (): boolean => {
    return Boolean(projectNameError);
  };

  const focusFirstInlineFormatField = () => {
    if (projectNameError && projectNameRef?.current) projectNameRef.current.focus();
  };

  const buildMappedPayload = (): any => ({
    projectName: projectName || '',
    valueCompassIds: Array.isArray(valueCompass) ? valueCompass.map(Number).filter((n) => !Number.isNaN(n)) : [],
    status: (status?.value ?? status?.id ?? status) || '',
    startDate: startDate ? convertDateToYYYYMMDD(startDate) : '',
    ccompletedDate: completedDate ? convertDateToYYYYMMDD(completedDate) : '',
    focusAreaId: mapOptionToId(focusArea),
    perfGoal: smartGoal || '',
    metricId: mapOptionToId(performanceMetric),
    otherMetricText: metricDescription || '',
    pspGoalId: toNumberOrNull(pspGoal) ?? null,
    dataSourceId: mapOptionToId(dataSource),
    otherDataSourceText: dataSourceDescription || '',
    baselineValue: toNumberOrNull(baseline),
    thresholdValue: toNumberOrNull(threshold),
    targetValue: toNumberOrNull(target),
    stretchValue: toNumberOrNull(stretch),
    listOfToolsUsed: Array.isArray(toolsSelected) ? toolsSelected.map((t) => (Number(t) || t)) : [],
    resultId: mapOptionToId(resultSummary),
    pipResultDesc: resultDescription || '',
    otherResultText: '',
    annualRoi: toNumberOrNull(annualRoi),
    perfLearnings: learnings || '',
    numeratorName: numerator || '',
    denominatorName: denominator || '',
    valueName: calculatedValue || '',
    outsideVarName: additionalVar || '',
    teamUrl: teamDocs || '',
  });

  const callOnSave = async (payload: any) => {
    try {
      const res = await onSave?.(payload);
      return res;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Save handler failed:', err);
      return { success: false, error: err };
    }
  };



  const containerRef = useRef<HTMLDivElement | null>(null);
  const errorBannerRef = useRef<HTMLDivElement | null>(null);
  // prevent repeated prepopulation runs causing update loops
  const prepopulatedRef = useRef(false);
  // Tracks the perfId of the last project that was successfully prepopulated.
  // Used to detect project switches while isEditing stays true.
  const lastPrepopulatedProjectIdRef = useRef<string | null>(null);
  // Allow dependent dropdown auto-selection only during initial edit-mode population.
  const allowDependentAutoSelectionRef = useRef(Boolean(isEditing));
  // track last focus id we notified parent about to avoid notification loops
  const lastNotifiedFocusIdRef = useRef<string | null>(null);
  // track last metric id we notified parent about to avoid notification loops
  const lastNotifiedMetricIdRef = useRef<string | null>(null);
  // track last data source id we notified parent about to avoid notification loops
  const lastNotifiedDataIdRef = useRef<string | null>(null);

  // When switching into Create (isEditing=false), clear all fields so the
  // Manage view presents a blank form. This handles the "Add new project" flow
  // when the parent may still hold previously selected `project`/`selectedProject`.
  useEffect(() => {
    if (isEditing) return;
    prepopulatedRef.current = false;
    allowDependentAutoSelectionRef.current = false;
    lastNotifiedFocusIdRef.current = null;
    lastNotifiedMetricIdRef.current = null;
    lastNotifiedDataIdRef.current = null;
    setIsPrepopulationComplete(false);
    initialFormSnapshotRef.current = null;
    isFormOpenRef.current = false;
    setProjectName('');
    setProjectNameError('');
    setValueCompass([]);
    setStatus(statusOptions[0]);
    setFocusArea(defaultOption);
    setStartDate(undefined);
    setCompletedDate(undefined);
    setSmartGoal('');
    setPerformanceMetric(defaultOption);
    setPspGoal('');
    setDataSource(defaultOption);
    setMetricDescription('');
    setDataSourceDescription('');
    setBaseline('');
    setTarget('');
    setThreshold('');
    setStretch('');
    setToolsSelected([]);
    setResultSummary(defaultResultSummaryOption ?? defaultOption);
    setResultDescription('');
    setAnnualRoi('');
    setAnnualRoiFormatError('');
    setLearnings('');
    setNumerator('');
    setDenominator('');
    setCalculatedValue('');
    setAdditionalVar('');
    setTeamDocs('');
    setTeamDocsError('');
    setValidationErrors([]);
    // Ensure any late effects don't override the cleared metric/data selection
    setTimeout(() => {
      try {
        setPerformanceMetric(defaultOption);
        setDataSource(defaultOption);
      } catch {}
    }, 0);
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    allowDependentAutoSelectionRef.current = true;
  }, [isEditing]);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const expandAllToggles = () => {
      const toggles = Array.from(containerRef.current?.querySelectorAll<HTMLElement>('.ds-contenttoggle') ?? []);
      toggles.forEach((toggle) => {
        const heading = toggle.querySelector<HTMLElement>('.ds-contenttoggle__expandable-heading');
        const content = toggle.querySelector<HTMLElement>('.ds-contenttoggle__content');
        if (!heading || !content) return;
        const isOpen = content.clientHeight > 0 || toggle.classList.contains('ds-contenttoggle__is-open');
        if (!isOpen) {
          heading.click();
        }
      });
    };

    expandAllToggles();
    const animationFrame = requestAnimationFrame(expandAllToggles);
    const retryTimer = setTimeout(expandAllToggles, 0);

    // focus first toggle heading after open actions complete
    const focusFrame = requestAnimationFrame(() => {
      const firstToggle = containerRef.current?.querySelector<HTMLElement>('.ds-contenttoggle') ?? null;
      const firstHeading = firstToggle?.querySelector<HTMLElement>('.ds-contenttoggle__expandable-heading') ?? null;
      if (firstHeading && typeof firstHeading.focus === 'function') {
        try {
          firstHeading.focus();
        } catch {
          setTimeout(() => firstHeading.focus(), 0);
        }
      }
    });

    return () => {
      cancelAnimationFrame(animationFrame);
      cancelAnimationFrame(focusFrame);
      clearTimeout(retryTimer);
    };
  }, []);

  const actionButtons = (
    <>
      <button type="button" className="button py-0! -inverted" onClick={() => {
        resetUnsavedChanges();
        if (typeof onClose === 'function') {
          onClose();
          return;
        }
        setProjectName(''); setSmartGoal('');
      }}>Cancel</button>
      <button type="button" className="button py-0! -primary -light" onClick={handleSave}>Save</button>
    </>
  );

  return (
    <div ref={containerRef}>
      {validationErrors.length > 0 && (
        <div ref={errorBannerRef} className="mb-4! mt-4!">
          <ErrorBanner errors={validationErrors} onErrorClick={handleBannerErrorClick} />
        </div>
      )}
      <ContentToggle titleText={'1. What are we trying to accomplish?'} accordionOption>
        <div>
          <div className="ds-form mb-8!" data-ds-theme="vessel" data-ds-variant="input">
            <label htmlFor="td-project-name">Name of project<span className="text-red-600 ml-1">*</span></label>
            <input ref={projectNameRef} id="td-project-name"
              required
              value={projectName}
              className={`mt-1! bg-white w-1/2! border border-border-light rounded-md p-2 ${hasFieldError('Name of project') || Boolean(projectNameError) ? 'field-error' : ''}`}
              onChange={(e) => {
                const rawValue = e.target.value;
                const nextValue = rawValue.slice(0, PROJECT_NAME_MAX);
                setProjectName(nextValue);
                setProjectNameError(rawValue.length > PROJECT_NAME_MAX ? PROJECT_NAME_MAX_ERROR : '');
                if (nextValue.trim()) {
                  clearFieldValidationError('Name of project');
                }
              }}
            />
            {projectNameError || hasFieldError('Name of project') ? (
              <div className="error-description" id="td-project-name-helper">
                <ul>
                  <li>
                    <img src="/assets/images/errorCircleSmall.svg" alt="Error icon" width="14" height="14" />
                    <span className="screenreader-only">Error</span>
                    {projectNameError || getFieldErrorMessage('Name of project')}
                  </li>
                </ul>
              </div>
            ) : (
              <div className="text-xs mt-1! text-border-gray!" id="td-project-name-helper">
                Required Field
              </div>
            )}
          </div>

          <div className="flex mb-8 gap-20!">
              <div className="flex-1 ds-form mb-8! max-w-80!" data-ds-theme="vessel" data-ds-variant="input">
              <div className="flex gap-1 mb-2">
                <label htmlFor="td-value-compass" className="flex items-center">Value Compass<span className="text-red-600 ml-1">*</span></label>
                <PopUp message={valueCompassHelperTextContent} className="self-start pt-0.5" />
              </div>
              <div ref={valueCompassRef}>
                <MultiCheckboxSelect
                  className="mt-1 w-80!"
                  options={valueCompassOptions}
                  selectedValue={valueCompass}
                  onChange={(selected) => {
                    setValueCompass(selected);
                    if (Array.isArray(selected) && selected.length > 0) {
                      clearFieldValidationError('Value Compass');
                    }
                  }}
                  isSelectAll={false}
                />
              </div>
              {hasFieldError('Value Compass') ? (
                <div className="error-description" id="td-value-compass-helper">
                  <ul>
                    <li>
                      <img src="/assets/images/errorCircleSmall.svg" alt="Error icon" width="14" height="14" />
                      <span className="screenreader-only">Error</span>
                      {getFieldErrorMessage('Value Compass')}
                    </li>
                  </ul>
                </div>
              ) : (
                <div className="text-xs mt-1! text-border-gray!" id="td-value-compass-helper">
                  Required Field
                </div>
              )}
            </div>

            <div className="flex-1 ds-form mb-10! max-w-70!" data-ds-theme="vessel" data-ds-variant="input">
              <label htmlFor="td-status" className="mb-2">Status</label>
              <div className="w-70! mb-4!">
                <Dropdown
                  selectedValue={status}
                  buttonClassName="w-70! bg-white"
                  options={statusOptions}
                  onSelect={(o) =>{
                      setStatus(o ?? defaultOption)
                  }}
                  menuClassName="w-full! max-h-65! overflow-y-auto!"
                  />
              </div>

              <div className="mt-2 ds-form mb-8!" data-ds-theme="vessel" data-ds-variant="input">
                <div className="flex gap-1 mb-2">
                  <label htmlFor="td-focus-area" className="flex items-center">Focus Area<span className="text-red-600 ml-1">*</span></label>
                  <PopUp message={focusAreaHelperTextContent} className="self-start pt-0.5" />
                </div>
                <div className="w-70!">
                  <div ref={focusAreaRef}>
                    <Dropdown
                      selectedValue={focusArea}
                      buttonClassName="w-70! bg-white"
                      options={focusAreaOptions}
                      onSelect={(o) => {
                        allowDependentAutoSelectionRef.current = false;
                        const selected = o ?? defaultOption;
                        const focusChanged = getOptionKey(selected) !== getOptionKey(focusArea);
                        setFocusArea(selected);
                        if (focusChanged) {
                          setPerformanceMetric(defaultOption);
                          setMetricDescription('');
                          setPspGoal('');
                          setDataSource(defaultOption);
                          setDataSourceDescription('');
                          clearFieldValidationError('Performance metric');
                          clearFieldValidationError('Data source for metric');
                          clearFieldValidationError('Metric description');
                          clearFieldValidationError('Data Source Description');
                        }
                        if (String(selected?.value ?? selected?.id ?? '') !== String(defaultOption.value)) {
                          clearFieldValidationError('Focus Area');
                        }
                        const focusId = String((selected as any).value ?? (selected as any).id ?? '');
                        if (onFocusAreaChange) onFocusAreaChange(focusId);
                      }} 
                      helperText='Required Field'
                      menuClassName="w-full! max-h-65! overflow-y-auto!"
                    />
                  </div>
                  {hasFieldError('Focus Area') ? (
                    <div className="error-description">
                      <ul>
                        <li>
                          <img src="/assets/images/errorCircleSmall.svg" alt="Error icon" width="14" height="14" />
                          <span className="screenreader-only">Error</span>
                          {getFieldErrorMessage('Focus Area')}
                        </li>
                      </ul>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="w-70! ds-form mb-8!" data-ds-theme="vessel" data-ds-variant="input">
              <div className="w-70! mb-6!">
              <label htmlFor="td-start-date">Start Date<span className="text-red-600 ml-1">*</span></label>
              <div ref={startDateRef}>
                <CustomDatepicker
                  name="startDate"
                  value={startDate}
                  onChange={(_name: string, value: string) => {
                    setStartDate(value);
                    if (value.trim()) {
                      clearFieldValidationError('Start Date');
                      setStartDateError('');
                    }
                    const validation = validateDate(value);
                    setStartDateError(validation.error);
                  }}
                  helperText='Required Field - MM-DD-YYYY'
                  error={startDateError || getFieldErrorMessage('Start Date')}
                />
              </div>
              </div>

            {(status?.value === 'Closed' || status?.id === 'Closed') && (
              <div>
                <label htmlFor="td-completed-date">Completed Date<span className="text-red-600 ml-1">*</span></label>
                <div ref={completedDateRef}>
                  <CustomDatepicker
                    name="completedDate"
                    value={completedDate}
                    onChange={(_name: string, value: string) => {
                      setCompletedDate(value);
                      const validation = validateDate(value);
                      if (value.trim() && validation.isValid) {
                        clearFieldValidationError('Completed Date');
                        setCompletedDateError('');
                      }
                      setCompletedDateError(validation.error);
                    }}
                    helperText='Required Field - MM-DD-YYYY'
                    error={completedDateError || getFieldErrorMessage('Completed Date')}
                  />
                </div>
              </div>
            )}
            </div>
          </div>

            <div className="ds-form w-full! mt-8! mb-4!" data-ds-theme="vessel" data-ds-variant="input">
            <div className="flex gap-1 mb-2">
              <label htmlFor="td-smart-goal" className="flex items-center">Our SMART goal<span className="text-red-600 ml-1">*</span></label>
              <PopUp message={smartGoalHelperTextContent} className="self-start pt-0.5" />
            </div>
            <div className="textarea-wrapper w-full relative">
              <textarea ref={smartGoalRef} id="td-smart-goal" required
                value={smartGoal}
                onChange={(e) => {
                  const nextValue = e.target.value.slice(0, FIELD_MAX);
                  setSmartGoal(nextValue);
                  if (nextValue.trim()) {
                    clearFieldValidationError('Our SMART goal');
                  }
                }}
                rows={4}
                maxLength={FIELD_MAX}
                className={`mt-1 bg-white w-full! border border-border-light rounded-md p-2 ${hasFieldError('Our SMART goal') ? 'field-error' : ''}`}
                style={{ width: '100%' }}
              />
            </div>
            {hasFieldError('Our SMART goal') ? (
              <div className="error-description">
                <ul>
                  <li>
                    <img src="/assets/images/errorCircleSmall.svg" alt="Error icon" width="14" height="14" />
                    <span className="screenreader-only">Error</span>
                    {getFieldErrorMessage('Our SMART goal')}
                  </li>
                </ul>
              </div>
            ) : null}
            <output className="ds-form__text-input-helper mt-1" aria-live="polite">
              Required Field - {getRemainingCharacterText(smartGoal, FIELD_MAX)}
            </output>
          </div>
        </div>
      </ContentToggle>
      <hr className="hr-light" />

      <ContentToggle titleText={'2. How will we know that change is an improvement?'} accordionOption={false}>
        <div className="mt-4 ds-form mb-8!" data-ds-theme="vessel" data-ds-variant="input">
          <div className="flex mb-8! gap-4!">
            <div className="flex-1 max-w-70!">
                <div className="flex gap-1 mb-2">
                  <label htmlFor="td-performance-metric" className="flex items-center">Performance metric<span className="text-red-600 ml-1">*</span></label>
                  <PopUp message={performanceMetricHelperTextContent} className="self-start pt-0.5" />
                </div>
              <div ref={performanceMetricRef}>
                <Dropdown
                  selectedValue={performanceMetric}
                  options={performanceMetricOptions}
                  buttonClassName="w-70! bg-white"
                  buttonLabel={performanceMetric?.label}
                  disabled={!hasSelectedFocusArea || performanceMetricOptions.length <= 1}
                  onSelect={(o) => {
                    allowDependentAutoSelectionRef.current = false;
                    const selected = o ?? defaultOption;
                    const metricChanged = getOptionKey(selected) !== getOptionKey(performanceMetric);
                    setPerformanceMetric(selected);
                    if (metricChanged) {
                      setDataSource(defaultOption);
                      setDataSourceDescription('');
                      clearFieldValidationError('Data source for metric');
                      clearFieldValidationError('Data Source Description');
                    }
                    if (isDefaultDropdownOption(selected)) {
                      setMetricDescription('');
                      setPspGoal('');
                      clearFieldValidationError('Metric description');
                    } else {
                      clearFieldValidationError('Performance metric');
                    }
                    const metricId = toNumberOrNull((selected as any).value ?? (selected as any).id ?? '');
                    if (onMetricChange && metricId !== null) onMetricChange(metricId);
                  }}
                  helperText='Select the Focus Area to get list of Performance. Metrics-Required Field.'
                />
              </div>
            {hasFieldError('Performance metric') ? (
              <div className="error-description">
                <ul>
                  <li>
                    <img src="/assets/images/errorCircleSmall.svg" alt="Error icon" width="14" height="14" />
                    <span className="screenreader-only">Error</span>
                    {getFieldErrorMessage('Performance metric')}
                  </li>
                </ul>
              </div>
            ) : null}
            </div>
            <div className="w-1/3 ds-form" data-ds-theme="vessel" data-ds-variant="input">
              <label htmlFor="td-pspGoal-roi">PSP Goal:</label>
                {!pspGoal && (
                  <div className="text-xs mt-1! text-border-gray!" id="td-start-date-helper">
                    Select Focus Area to view PSP Goal.
                  </div>
                )}
                {pspGoal && (
                  <div className="text-xs mt-1!">
                    {pspGoal}
                  </div>
                )}
            </div>
          </div>

          {showOtherMetric && (
            <div className="ds-form mb-4! mt-4!" data-ds-theme="vessel" data-ds-variant="input">
              <ErrorField
                id="td-metric-description"
                value={metricDescription}
                onChange={(e) => setMetricDescription(e.target.value.slice(0, FIELD_MAX))}
                error={hasFieldError('Metric description')}
                errorMessage={getFieldErrorMessage('Metric description')}
                textareaRef={metricDescriptionRef}
                helperText={
                  <output className="ds-form__text-input-helper mt-1" aria-live="polite">
                    {getRemainingCharacterText(metricDescription, FIELD_MAX)}
                  </output>
                }
                label={<>
                  Metric description<span className="text-red-600 ml-1">*</span>
                </>}
              />
            </div>
          )}

          <div className="mb-8!">
            <label htmlFor="td-data-source" className="mb-2">Data source for metric<span className="text-red-600 ml-1">*</span></label>
              <div ref={dataSourceRef}>
                <Dropdown 
                  selectedValue={dataSource} 
                  buttonClassName="w-70! bg-white" 
                  options={dataSourceOptionsFromProp} 
                  buttonLabel={dataSource?.label}
                  disabled={!hasSelectedFocusArea || !hasSelectedPerformanceMetric || (dataSourceOptionsFromProp.length <= 1 && getOptionKey(dataSource) === getOptionKey(defaultOption))}
                  menuClassName="w-70! max-h-65! overflow-y-auto!"
                  onSelect={(o) => {
                    allowDependentAutoSelectionRef.current = false;
                    const selected = o ?? defaultOption;
                    setDataSource(selected);
                    if (isDefaultDropdownOption(selected)) {
                      setDataSourceDescription('');
                      clearFieldValidationError('Data Source Description');
                      return;
                    }
                    clearFieldValidationError('Data source for metric');
                  }} 
                  helperText='Select the Performance Metric to get list of Data. Source-Required Field.'
                />
              </div>
            {hasFieldError('Data source for metric') ? (
              <div className="error-description">
                <ul>
                  <li>
                    <img src="/assets/images/errorCircleSmall.svg" alt="Error icon" width="14" height="14" />
                    <span className="screenreader-only">Error</span>
                    {getFieldErrorMessage('Data source for metric')}
                  </li>
                </ul>
              </div>
            ) : null}
            {/* <div className="helper-text text-sm text-gray-500 mt-1" id="td-start-date-helper">
              <span>Select the Performance Metric to get list of Data.</span>
                <br />
                <span>Source-Required Field.</span>
            </div> */}
          </div>

          {showDataSourceDescription && (
            <div className="ds-form mb-4! mt-4!" data-ds-theme="vessel" data-ds-variant="input">
              <ErrorField
                id="td-data-source-description"
                value={dataSourceDescription}
                onChange={(e) => setDataSourceDescription(e.target.value.slice(0, FIELD_MAX))}
                error={hasFieldError('Data Source Description')}
                errorMessage={getFieldErrorMessage('Data Source Description')}
                textareaRef={dataSourceDescriptionRef}
                helperText={
                  <output className="ds-form__text-input-helper mt-1" aria-live="polite">
                    {getRemainingCharacterText(dataSourceDescription, FIELD_MAX)}
                  </output>
                }
                label={<>
                  Data Source Description<span className="text-red-600 ml-1">*</span>
                </>}
              />
            </div>
          )}

           <div className="flex gap-1 mb-4">
            <label htmlFor="td-data-goal" className="flex items-center">Goal Setting</label>
            <PopUp message={goalSettingHelperTextContent} className="self-start pt-0.5" />
            </div>
          <div className="flex gap-x-4 mt-4 w-full!">
            <div className="ds-form w-full! mb-4!" data-ds-theme="vessel" data-ds-variant="input">
              <label htmlFor="td-baseline">Baseline</label>
              <input id="td-baseline"
                ref={baselineRef}
                value={baseline}
                className={`mt-1 bg-white w-full! border border-border-light rounded-md p-2 ${baselineFormatError ? 'field-error' : ''}`}
                onChange={(e) => {
                  const { value, error } = normalizeGoalInput(e.target.value);
                  setBaseline(value);
                  setBaselineFormatError(error);
                }}
              />
              {baselineFormatError ? (
                <div className="error-description">
                  <ul>
                    <li>
                      <img src="/assets/images/errorCircleSmall.svg" alt="Error icon" width="14" height="14" />
                      <span className="screenreader-only">Error</span>
                      {baselineFormatError}
                    </li>
                  </ul>
                </div>
              ) : null}
            </div>
            <div className="ds-form w-full! mb-4!" data-ds-theme="vessel" data-ds-variant="input">
              <label htmlFor="td-threshold">Threshold</label>
              <input id="td-threshold"
                ref={thresholdRef}
                value={threshold}
                onChange={(e) => {
                  const { value, error } = normalizeGoalInput(e.target.value);
                  setThreshold(value);
                  setThresholdFormatError(error);
                }}
                className={`mt-1 bg-white w-full! border border-border-light rounded-md p-2 ${thresholdFormatError ? 'field-error' : ''}`} />
              {thresholdFormatError ? (
                <div className="error-description">
                  <ul>
                    <li>
                      <img src="/assets/images/errorCircleSmall.svg" alt="Error icon" width="14" height="14" />
                      <span className="screenreader-only">Error</span>
                      {thresholdFormatError}
                    </li>
                  </ul>
                </div>
              ) : null}
            </div>
            <div className="ds-form w-full! mb-4!" data-ds-theme="vessel" data-ds-variant="input">
              <label htmlFor="td-target">Target</label>
              <input id="td-target"
                ref={targetRef}
                value={target}
                onChange={(e) => {
                  const { value, error } = normalizeGoalInput(e.target.value);
                  setTarget(value);
                  setTargetFormatError(error);
                }}
                className={`mt-1 bg-white w-full! border border-border-light rounded-md p-2 ${targetFormatError ? 'field-error' : ''}`} />
              {targetFormatError ? (
                <div className="error-description">
                  <ul>
                    <li>
                      <img src="/assets/images/errorCircleSmall.svg" alt="Error icon" width="14" height="14" />
                      <span className="screenreader-only">Error</span>
                      {targetFormatError}
                    </li>
                  </ul>
                </div>
              ) : null}
            </div>
            <div className="ds-form w-full! mb-4!" data-ds-theme="vessel" data-ds-variant="input">
              <label htmlFor="td-stretch">Stretch</label>
              <input id="td-stretch"
                ref={stretchRef}
                value={stretch}
                onChange={(e) => {
                  const { value, error } = normalizeGoalInput(e.target.value);
                  setStretch(value);
                  setStretchFormatError(error);
                }} 
                className={`mt-1 bg-white w-full! border border-border-light rounded-md p-2 ${stretchFormatError ? 'field-error' : ''}`} />
              {stretchFormatError ? (
                <div className="error-description">
                  <ul>
                    <li>
                      <img src="/assets/images/errorCircleSmall.svg" alt="Error icon" width="14" height="14" />
                      <span className="screenreader-only">Error</span>
                      {stretchFormatError}
                    </li>
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </ContentToggle>
      <hr className="hr-light" />

      <ContentToggle titleText={'3. What changes can we make that result in improvement?'} accordionOption={false}>
        <div className="mt-4 gap-x-8! ">
          <div className="flex gap-x-20! ">
            <div className="flex-1 max-w-100! ds-form">
              <div className="flex gap-1 mb-4">
                <label htmlFor="td-tools-using" className="flex items-center">What tools are we using?</label>
                <PopUp message={toolUsingHelperText} className="self-start pt-0.5" />
              </div>
              <MultiCheckboxSelect
                options={toolsOptions}
                selectedValue={toolsSelected}
                onChange={(s) => setToolsSelected(s)}
                isSelectAll={false}
              />
            </div>

            <div className="flex gap-x-20! mt-4 ds-form" data-ds-theme="vessel" data-ds-variant="input">
              <div>
                <div className="flex gap-1 mb-2">
                  <label htmlFor="td-ideas-ubts" className="flex items-center">Ideas from UBTs Doing Similar Work?</label>
                  <PopUp message={ideaHelperText} className="self-start pt-0.5" />
                </div>
                <ul id="td-ideas-ubts" className="mt-2 list-disc pl-6">
                  {resourceLinks.map((r) => (
                    <li key={r.label}>
                      <a
                        href={normalizeExternalUrl(r.href)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline text-kp-blue-dark hover:text-kp-blue cursor-pointer"
                        onClick={(event) => openExternalLink(event, r.href)}
                      >
                        {r.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-4">
                <div className="flex items-center gap-1">
                  <label htmlFor="td-tools-tips-link" className="flex items-center">Tools & Tip</label>
                  <PopUp message={toolAndTipsHelperText} />
                </div>
                {toolsAndTipsLink && (
                  <a
                    href={normalizeExternalUrl(toolsAndTipsLink.href)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-kp-blue-dark hover:text-kp-blue cursor-pointer"
                    onClick={(event) => openExternalLink(event, toolsAndTipsLink.href)}
                  >
                    {toolsAndTipsLink.label}
                  </a>
                )}
              </div>

              <div className="mt-4">
                <div className="flex items-center gap-1">
                  <label htmlFor="td-metric-report-link" className="flex items-center">Link to Report for Metric</label>
                  <PopUp message={linkToMetricsInformation} />
                </div>
                {reportLink && (
                  <a
                    href={normalizeExternalUrl(reportLink.href)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-kp-blue-dark hover:text-kp-blue cursor-pointer"
                    onClick={(event) => openExternalLink(event, reportLink.href)}
                  >
                    {reportLink.label}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </ContentToggle>
      <hr className="hr-light" />

      <ContentToggle titleText={'4. What results did we obtain?'} accordionOption={false}>
        <div className="mt-4">
          <div className="flex gap-4!">
            <div className="mt-1 w-80!">
              <Dropdown
                label="Result Summary"
                popupMessage={resultSummaryHelperText}
                selectedValue={resultSummary}
                placeholder=""
                buttonLabel={
                  hasValidResultSummarySelection
                    ? resultSummary?.label
                    : (defaultResultSummaryOption?.label ?? '')
                }
                options={resultSummaryOptions}
                buttonClassName="w-80! max-w-80! bg-white"
                onSelect={(o) => setResultSummary(o ?? defaultResultSummaryOption ?? defaultOption)}
              />
            </div>
            <div className="ds-form w-80! mb-4!" data-ds-theme="vessel" data-ds-variant="input">
              <div className="flex gap-1 mb-2">
                <label htmlFor="td-annual-roi" className="flex items-center">Annual ROI</label>
                <PopUp message={annualROIHelperText} className="self-start pt-0.5" />
              </div>
              <input id="td-annual-roi"
                ref={annualRoiRef}
                value={annualRoi}
                onChange={(e) => {
                  let value = e.target.value.replaceAll(/[^\d.]/g, '');
                  const parts = value.split('.');
                  if (parts.length > 2) {
                    value = parts[0] + '.' + parts.slice(1).join('');
                  }
                  let [intPart, fracPart] = value.split('.');
                  let error = '';
                  if (intPart && intPart.length > ANNUAL_ROI_MAX_INTEGER_DIGITS) {
                    error = ANNUAL_ROI_FORMAT_ERROR;
                    intPart = intPart.slice(0, ANNUAL_ROI_MAX_INTEGER_DIGITS);
                  }
                  if (typeof fracPart === 'string' && fracPart.length > DECIMAL_MAX_FRACTION_DIGITS) {
                    error = ANNUAL_ROI_FORMAT_ERROR;
                    fracPart = fracPart.slice(0, DECIMAL_MAX_FRACTION_DIGITS);
                  }
                  value = fracPart === undefined ? intPart : `${intPart}.${fracPart}`;
                  setAnnualRoi(value);
                  setAnnualRoiFormatError(error);
                }}
                className={`mt-1 bg-white w-80! border border-border-light rounded-md p-2 ${annualRoiFormatError ? 'field-error' : ''}`}  />
              {annualRoiFormatError ? (
                <div className="error-description">
                  <ul>
                    <li>
                      <img src="/assets/images/errorCircleSmall.svg" alt="Error icon" width="14" height="14" />
                      <span className="screenreader-only">Error</span>
                      {annualRoiFormatError}
                    </li>
                  </ul>
                </div>
              ) : null}
            </div>
          </div>

          <div className="ds-form mb-4! mt-4!" data-ds-theme="vessel" data-ds-variant="input">
            <div className="flex gap-1 mb-2">
              <label htmlFor="td-result-description" className="flex items-center">Result description</label>
              <PopUp message={resultDescHelperText} className="self-start pt-0.5" />
            </div>
            <ErrorField
              id="td-result-description"
              value={resultDescription}
              onChange={(e) => {
                setResultDescription(e.target.value.slice(0, FIELD_MAX));
                if (e.target.value.trim()) {
                  clearFieldValidationError('Result description');
                } else if (status?.value === 'Closed' || status?.id === 'Closed') {
                  setValidationErrors((prev) => [
                    ...prev.filter((err) => err.field !== 'Result description'),
                    {
                      field: 'Result description',
                      message: 'Result description is required.',
                      fieldRef: resultDescriptionRef,
                    },
                  ]);
                }
              }}
              error={hasFieldError('Result description')}
              errorMessage={getFieldErrorMessage('Result description')}
              textareaRef={resultDescriptionRef}
              helperText={
                <output className="ds-form__text-input-helper mt-1" aria-live="polite">
                  {getRemainingCharacterText(resultDescription, FIELD_MAX)}
                </output>
              }
            />
          </div>

          <div className="ds-form mb-4! mt-4!" data-ds-theme="vessel" data-ds-variant="input">
            <div className="flex gap-1 mb-2">
              <label htmlFor="td-learnings" className="flex items-center">Learning or successful practices</label>
              <PopUp message={learningPracticeHelperText} className="self-start pt-0.5" />
            </div>
            <ErrorField
              id="td-learnings"
              value={learnings}
              onChange={(e) => {
                setLearnings(e.target.value.slice(0, FIELD_MAX));
                if (e.target.value.trim()) {
                  clearFieldValidationError('Learning or successful practices');
                } else if (status?.value === 'Closed' || status?.id === 'Closed') {
                  setValidationErrors((prev) => [
                    ...prev.filter((err) => err.field !== 'Learning or successful practices'),
                    {
                      field: 'Learning or successful practices',
                      message: 'Learning or successful practices is required.',
                      fieldRef: learningsRef,
                    },
                  ]);
                }
              }}
              error={hasFieldError('Learning or successful practices')}
              errorMessage={getFieldErrorMessage('Learning or successful practices')}
              textareaRef={learningsRef}
              helperText={
                <output className="ds-form__text-input-helper mt-1" aria-live="polite">
                  {getRemainingCharacterText(learnings, FIELD_MAX)}
                </output>
              }
            />
          </div>
        </div>
      </ContentToggle>
      <hr className="hr-light" />

      <ContentToggle titleText={'5. Define and track your own outcome measure'} accordionOption={false}>
        <div className="mt-4">
          <div className="flex gap-x-4">
            <div className="ds-form w-full! mb-4!" data-ds-theme="vessel" data-ds-variant="input">
              <div className="flex gap-1 mb-2">
                <label htmlFor="td-numerator" className="flex items-center">Numerator Label (Top)</label>
                <PopUp message={numeratoreHelperText} className="self-start pt-0.5" />
              </div>
              <input
                id="td-numerator"
                value={numerator}
                onChange={(e) => setNumerator(e.target.value.slice(0, OUTCOME_MEASURE_FIELD_MAX))}
                maxLength={OUTCOME_MEASURE_FIELD_MAX}
                className="mt-1 bg-white w-full! border border-border-light rounded-md p-2"  /> </div>

            <div className="ds-form w-full! mb-4!" data-ds-theme="vessel" data-ds-variant="input">
              <div className="flex gap-1 mb-2">
                <label htmlFor="td-calculated-value" className="flex items-center">Calculated Value Label (Top/Bottom)</label>
                <PopUp message={calculatedValueLabelHelperText} className="self-start pt-0.5" />
              </div>
              <input
                id="td-calculated-value"
                value={calculatedValue}
                onChange={(e) => setCalculatedValue(e.target.value.slice(0, OUTCOME_MEASURE_FIELD_MAX))}
                maxLength={OUTCOME_MEASURE_FIELD_MAX}
                className="mt-1 bg-white w-full! border border-border-light rounded-md p-2" /></div>

            <div className="ds-form w-full! mb-4!" data-ds-theme="vessel" data-ds-variant="input">
              <div className="flex gap-1 mb-2">
                <label htmlFor="td-denominator" className="flex items-center">Denominator Label (Bottom)</label>
                <PopUp message={denominatorHelperText} className="self-start pt-0.5" />
              </div>
              <input
                id="td-denominator"
                value={denominator}
                onChange={(e) => setDenominator(e.target.value.slice(0, OUTCOME_MEASURE_FIELD_MAX))}
                maxLength={OUTCOME_MEASURE_FIELD_MAX}
                className="mt-1 bg-white w-full! border border-border-light rounded-md p-2" />
            </div>


            <div className="ds-form w-full! mb-4!" data-ds-theme="vessel" data-ds-variant="input">
              <div className="flex gap-1 mb-2">
                <label htmlFor="td-additional-var" className="flex items-center">Additional Variable Label</label>
                <PopUp message={additionalVarialbeLabelHelperText} className="self-start pt-0.5" />
              </div>
              <input
                id="td-additional-var"
                value={additionalVar}
                onChange={(e) => setAdditionalVar(e.target.value.slice(0, OUTCOME_MEASURE_FIELD_MAX))}
                maxLength={OUTCOME_MEASURE_FIELD_MAX}
                className="mt-1 bg-white w-full! border border-border-light rounded-md p-2" />
            </div>
          </div>
        </div>
      </ContentToggle>
      <hr className="hr-light" />

      <ContentToggle titleText={'6. Project Information Documents'} accordionOption={false}>
        <div className="mt-4">
          <div className="ds-form w-full! mb-4!" data-ds-theme="vessel" data-ds-variant="input">
            <div className="flex gap-1 mb-2">
              <label htmlFor="td-team-docs" className="flex items-center">Team Project Documentation</label>
              <PopUp message={teamDocumentationHelperText} className="self-start pt-0.5" />
            </div>
            <input
              id="td-team-docs"
              ref={teamDocsRef}
              value={teamDocs}
              onChange={(e) => {
                const nextValue = e.target.value;
                setTeamDocs(nextValue);
                setTeamDocsError(validateExternalUrlInput(nextValue));
              }}
              className={`mt-1 bg-white w-150! border border-border-light rounded-md p-2 ${teamDocsError ? 'field-error' : ''}`}  />
            {teamDocsError ? (
              <div className="error-description" id="td-team-docs-helper">
                <ul>
                  <li>
                    <img src="/assets/images/errorCircleSmall.svg" alt="Error icon" width="14" height="14" />
                    <span className="screenreader-only">Error</span>
                    {teamDocsError}
                  </li>
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      </ContentToggle>
      <hr className="hr-light" />

        {actionContainerRef?.current
          ? createPortal(actionButtons, actionContainerRef.current)
          : <div className="flex gap-x-4 mt-6!">{actionButtons}</div>}
    </div>
  );
};

/**
 * Renders the manage project form and delegates stateful behavior to the internal implementation.
 * @param props - Container properties for managing project details.
 * @returns The manage project container component.
 */
const ManageProjectContainer: React.FC<ManageProjectContainerProps> = (props) => (
  <ManageProjectContainerContent {...props} />
);

export default ManageProjectContainer;

